import {PredicateOf} from '../../../../../libs/shared/helpers/src/react/reactHelpers';
import {DataFieldsTableUpdater} from '../../../../components/dataTable/useDataFieldsTableActions';
import {DataFieldsTableModel} from '../../../../models/api/dataFieldsTableModel';
import {EditorErrorsEnum} from '../../../editors/commons/editorErrors';
import {ConditionAction} from '../../../editors/conditions/conditionsActions';
import {ExpressionAction} from '../../../editors/expressions/expressionActions';
import {ClientRuleAction} from '../../ruleActionModel';
import {RuleIntegration} from '../../ruleIntegrationModel';
import {TriggerWithConditions} from '../../ruleTriggerHelpers';
import {RuleTriggersTypesEnum} from '../../ruleTriggerModel';
import {RuleEditorState} from './ruleEditorState';

/*
 * Type enum.
 */

export enum RuleEditorActionTypesEnum {
  // Name.
  UPDATE_RULE_NAME = '@@rule_editor/UPDATE_RULE_NAME',

  // Example resource ID
  UPDATE_EXAMPLE_RESOURCE_ID = '@@rule_editor/UPDATE_EXAMPLE_RESOURCE_ID',

  SET_DELETE_CONFIRMATION_OPEN = '@@rule_editor/OPEN_DELETE_CONFIRMATION',

  // Active.
  TOGGLE_IS_ACTIVE = '@@rule_editor/TOGGLE_IS_ACTIVE',
  // Stop processing.
  TOGGLE_STOP_PROCESSING = '@@rule_editor/TOGGLE_STOP_PROCESSING',
  // Errors.
  TOGGLE_ERROR = '@@rule_editor/TOGGLE_ERROR',
  SET_HIDDEN_ERRORS = '@@rule_editor/SET_HIDDEN_ERRORS',

  // Triggers.
  ADD_TRIGGER = '@@rule_editor/ADD_TRIGGER',
  UPDATE_TRIGGER = '@@rule_editor/UPDATE_TRIGGER',
  DELETE_TRIGGER = '@@rule_editor/DELETE_TRIGGER',

  // Actions.
  ADD_ACTION = '@@rule_editor/ADD_ACTION',
  UPDATE_ACTION = '@@rule_editor/UPDATE_ACTION',
  DELETE_ACTION = '@@rule_editor/DELETE_ACTION',

  // Rule Table
  UPDATE_TABLE = '@@rule_editor/UPDATE_TABLE',
  SET_TABLE = '@@rule_editor/SET_TABLE',
}

/*
 * Types.
 */

interface UpdateRuleName {
  type: RuleEditorActionTypesEnum.UPDATE_RULE_NAME;
  name: string;
}

interface UpdateExampleResourceId {
  type: RuleEditorActionTypesEnum.UPDATE_EXAMPLE_RESOURCE_ID;
  resourceId: string | undefined;
}

interface ToggleIsActive {
  type: RuleEditorActionTypesEnum.TOGGLE_IS_ACTIVE;
}

interface ToggleStopProcessing {
  type: RuleEditorActionTypesEnum.TOGGLE_STOP_PROCESSING;
}

interface ToggleErrorAction {
  type: RuleEditorActionTypesEnum.TOGGLE_ERROR;
  key: string;
  error?: EditorErrorsEnum;
}

interface SetHiddenErrors {
  type: RuleEditorActionTypesEnum.SET_HIDDEN_ERRORS;
  cards: ReadonlyArray<string>; // clientId or Trigger
}

interface AddTrigger {
  type: RuleEditorActionTypesEnum.ADD_TRIGGER;
  trigger: RuleTriggersTypesEnum;
  ruleIntegration: RuleIntegration;
}

interface UpdateTrigger {
  type: RuleEditorActionTypesEnum.UPDATE_TRIGGER;
  triggerWithConditions: TriggerWithConditions;
}

interface DeleteTrigger {
  type: RuleEditorActionTypesEnum.DELETE_TRIGGER;
  trigger: RuleTriggersTypesEnum;
}

interface AddAction {
  type: RuleEditorActionTypesEnum.ADD_ACTION;
  condition: ClientRuleAction;
}

interface UpdateAction {
  type: RuleEditorActionTypesEnum.UPDATE_ACTION;
  predicate: PredicateOf<ClientRuleAction>;
  update(action: ClientRuleAction): ClientRuleAction;
}

interface DeleteAction {
  type: RuleEditorActionTypesEnum.DELETE_ACTION;
  predicate: PredicateOf<ClientRuleAction>;
}

interface UpdateTable {
  type: RuleEditorActionTypesEnum.UPDATE_TABLE;
  updater: DataFieldsTableUpdater;
}

interface SetTable {
  type: RuleEditorActionTypesEnum.SET_TABLE;
  table: DataFieldsTableModel | undefined;
}

/*
 * Combined action type.
 */

type TriggerAction = AddTrigger | UpdateTrigger | DeleteTrigger;
type ActionAction = AddAction | UpdateAction | DeleteAction;

export type RuleEditorAction =
  | UpdateRuleName
  | UpdateExampleResourceId
  | ToggleIsActive
  | ToggleStopProcessing
  | ToggleErrorAction
  | SetHiddenErrors
  | TriggerAction
  | ConditionAction
  | ActionAction
  | UpdateTable
  | SetTable
  | ExpressionAction<RuleEditorState>;
